import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./LoanAgainstProperty.css";
import "animate.css/animate.min.css";
import Seo from "../components/Seo";

const lapUses = [
  {
    icon: "bi bi-briefcase",
    title: "Business Expansion",
    text:
      "Use eligible funding for working capital, equipment, inventory, office expansion or other business requirements.",
  },
  {
    icon: "bi bi-mortarboard",
    title: "Higher Education",
    text:
      "Manage eligible tuition, accommodation and education-related expenses without immediately using your entire savings.",
  },
  {
    icon: "bi bi-house-gear",
    title: "Property Renovation",
    text:
      "Finance eligible repairs, renovation, extension or improvement work for your residential or commercial property.",
  },
  {
    icon: "bi bi-heart",
    title: "Wedding Expenses",
    text:
      "Arrange funds for eligible wedding-related expenses through a secured loan based on your property and repayment profile.",
  },
  {
    icon: "bi bi-hospital",
    title: "Medical Requirements",
    text:
      "Access funds for eligible medical treatment, hospitalisation or long-term healthcare requirements.",
  },
  {
    icon: "bi bi-arrow-repeat",
    title: "Debt Consolidation",
    text:
      "Consolidate eligible high-cost obligations into a structured secured loan, subject to lender assessment.",
  },
];

const eligibilityItems = [
  {
    image: "/images/Bank Pages/salaried-image-icon.png",
    alt: "Loan Against Property applicant profile",
    title: "Applicant Profile",
    text:
      "Salaried, self-employed and eligible business applicants may apply.",
  },
  {
    image: "/images/Bank Pages/age.png",
    alt: "Loan Against Property age eligibility",
    title: "Applicant Age",
    text:
      "Age eligibility depends on the applicant profile, lender policy and requested tenure.",
  },
  {
    image: "/images/Bank Pages/3-month-experienced.png",
    alt: "Loan Against Property work experience",
    title: "Income Stability",
    text:
      "Applicants should generally have stable employment, professional income or business continuity.",
  },
  {
    image: "/images/Bank Pages/minimum-income_20000.png",
    alt: "Loan Against Property repayment capacity",
    title: "Repayment Capacity",
    text:
      "Eligibility depends on income, existing EMIs, credit history and other financial obligations.",
  },
];

const documentGroups = [
  {
    image: "/images/Bank Pages/photo-identy.png",
    title: "Identity Documents",
    items: [
      "PAN Card",
      "Aadhaar Card",
      "Passport",
      "Voter ID",
      "Driving Licence",
      "Recent passport-size photographs",
    ],
  },
  {
    image: "/images/Bank Pages/proof-of-residence.png",
    title: "Address Documents",
    items: [
      "Aadhaar Card",
      "Passport",
      "Driving Licence",
      "Registered rent agreement",
      "Recent electricity or utility bill",
      "Bank statement with current address",
    ],
  },
  {
    image: "/images/Bank Pages/income-proof.png",
    title: "Income Documents",
    items: [
      "Recent salary slips",
      "Salary or business bank statements",
      "Form 16, where applicable",
      "Income tax returns",
      "Profit and loss statement",
      "Audited balance sheet",
      "Business registration documents",
    ],
  },
  {
    image: "/images/Bank Pages/other-document.png",
    title: "Property Documents",
    items: [
      "Sale deed or title deed",
      "Property tax receipts",
      "Approved building plan",
      "Possession or occupancy certificate",
      "Society or builder documents",
      "Previous title documents",
      "Encumbrance or legal documents, where required",
    ],
  },
];

const applicationSteps = [
  {
    title: "Share Your Requirement",
    text:
      "Provide the required loan amount, property details, income profile and intended use of funds.",
  },
  {
    title: "Initial Eligibility Review",
    text:
      "Our team reviews your profile, repayment capacity and basic property information.",
  },
  {
    title: "Compare Lender Options",
    text:
      "Available lender options are compared based on indicative rates, tenure, charges and eligibility.",
  },
  {
    title: "Submit Documents",
    text:
      "Submit identity, address, income, banking and property documents with our assistance.",
  },
  {
    title: "Credit and Property Verification",
    text:
      "The lender completes credit assessment, legal verification and technical valuation of the property.",
  },
  {
    title: "Sanction and Disbursement",
    text:
      "After approval and completion of all lender conditions, the sanctioned amount is disbursed.",
  },
];

const lapRates = [
  {
    bank: "Bank of India",
    link: "https://bankofindia.co.in/star-loan-against-property",
    rate: "7.90%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "Central Bank of India",
    link: "https://www.centralbankofindia.co.in/en/Cent_Mortgage_msme",
    rate: "7.90%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "Bank of Maharashtra",
    link: "https://bankofmaharashtra.in/loan-against-property",
    rate: "7.90%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "HDFC Bank",
    link:
      "https://www.hdfcbank.com/personal/borrow/your-loans-against-securities/loan-against-property",
    rate: "8.00%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "Axis Bank",
    link:
      "https://www.axisbank.com/retail/loans/loan-against-property",
    rate: "9.25%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "IDFC FIRST Bank",
    link:
      "https://www.idfcfirstbank.com/personal-banking/loans/loan-against-property/lap-interest-rate",
    rate: "9.50%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "Kotak Mahindra Bank",
    link:
      "https://www.kotak.com/en/business/loans/loan-against-property.html",
    rate: "9.50%*",
    amount: "₹10 lakh–₹10 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "IDBI Bank",
    link: "https://www.idbibank.in/loan-against-property.aspx",
    rate: "9.50%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
  {
    bank: "ICICI Bank",
    link:
      "https://campaigns.icicibank.com/hl/loanAgainstProperty/main/index.html",
    rate: "11.10%*",
    amount: "₹10 lakh–₹25 crore",
    tenure: "Up to 25 years",
    fee: "As per bank policy",
  },
];

const faqItems = [
  {
    question: "What is a Loan Against Property?",
    answer:
      "A Loan Against Property is a secured loan in which an eligible residential, commercial or industrial property is provided as collateral. The loan amount and terms depend on the applicant and property assessment.",
  },
  {
    question: "Which properties may be accepted for a LAP?",
    answer:
      "Depending on lender policy, eligible self-occupied, rented, residential, commercial or industrial properties may be considered. Vacant land may not be accepted by every lender.",
  },
  {
    question: "How much funding can I receive against my property?",
    answer:
      "The eligible amount depends on the assessed property value, income, repayment capacity, credit profile, existing obligations and the lender’s loan-to-value policy.",
  },
  {
    question: "Can the funds be used for business or personal purposes?",
    answer:
      "LAP funds may generally be used for eligible business, education, medical, wedding, renovation or personal requirements. Certain restricted uses may apply under lender policy.",
  },
  {
    question: "Which documents are required?",
    answer:
      "Common requirements include PAN, Aadhaar, bank statements, income documents, employment or business proof and complete ownership and title documents of the property.",
  },
  {
    question: "How long does LAP approval take?",
    answer:
      "The processing period depends on document completeness, income verification, credit assessment, legal review and technical valuation of the property.",
  },
  {
    question: "What interest rate will I receive?",
    answer:
      "The final rate depends on the selected lender, applicant profile, property type, loan amount, credit score and prevailing lender policies. Displayed rates are indicative.",
  },
  {
    question: "Can I continue using the mortgaged property?",
    answer:
      "In most cases, the borrower continues to use or occupy the property while the loan is active, provided repayment and all loan conditions are maintained.",
  },
  {
    question: "Can an existing Loan Against Property be transferred?",
    answer:
      "Eligible borrowers may transfer an existing LAP to another lender for potentially improved rates or repayment terms, subject to lender assessment and charges.",
  },
  {
    question: "Are processing and property-verification charges applicable?",
    answer:
      "The lender may charge processing, legal, technical valuation, documentation and other applicable fees. Exact charges should be confirmed before application.",
  },
];

function LoanAgainstProperty() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <Seo
        title="Loan Against Property in Pune | MONAYRA (OPC) PRIVATE LIMITED"
        description="Explore Loan Against Property options in Pune with MONAYRA (OPC) PRIVATE LIMITED. Get support with lender comparison, eligibility, documentation, property verification and loan processing."
        keywords="Loan Against Property in Pune, LAP loan, mortgage loan Pune, property loan eligibility, apply LAP online"
        canonical="https://www.mortbuzz.in/mortbuzz-loan-against-property"
      />

      <main className="monayra-lap-page">
        {/* HERO */}

        <section className="lap-hero">
          <div className="lap-hero-overlay"></div>

          <div className="lap-hero-container">
            <span className="lap-hero-eyebrow animate__animated animate__fadeInDown">
              Secured Property Finance
            </span>

            <h1 className="animate__animated animate__fadeInUp">
              Unlock the financial value of
              <span> your property</span>
            </h1>

            <p className="animate__animated animate__fadeInUp animate__delay-1s">
              Explore suitable Loan Against Property options with professional
              assistance for eligibility, documentation, property verification
              and lender processing.
            </p>

            <div className="lap-hero-actions animate__animated animate__fadeInUp animate__delay-1s">
              <Link to="/apply-now" className="lap-primary-btn">
                Apply for LAP
                <i className="bi bi-arrow-right"></i>
              </Link>

              <a href="#lap-rates" className="lap-secondary-btn">
                <i className="bi bi-bar-chart"></i>
                View Indicative Rates
              </a>
            </div>

            <div className="lap-hero-highlights">
              <div>
                <i className="bi bi-building"></i>
                Secured property finance
              </div>

              <div>
                <i className="bi bi-bank"></i>
                Multiple lender options
              </div>

              <div>
                <i className="bi bi-file-earmark-check"></i>
                Documentation assistance
              </div>
            </div>
          </div>
        </section>

        {/* INTRO */}

        <section className="lap-intro-section">
          <div className="lap-page-container lap-intro-grid">
            <div className="lap-intro-image">
              <img
                src="/images/FrontPage/Loan-Against-Properties.jpg"
                alt="Loan Against Property assistance"
              />

              <div className="lap-intro-image-overlay"></div>

              <div className="lap-rate-badge">
                <span>Indicative starting rate</span>
                <strong>7.90%*</strong>
                <small>Subject to lender assessment</small>
              </div>
            </div>

            <div className="lap-intro-content">
              <span className="lap-section-eyebrow">
                Why Choose MONAYRA?
              </span>

              <h2>
                Turn your eligible property into
                <span> financial opportunity</span>
              </h2>

              <p>
                A Loan Against Property is a secured loan that may allow you to
                raise funds by offering an eligible residential, commercial or
                industrial property as collateral.
              </p>

              <p>
                MONAYRA (OPC) PRIVATE LIMITED assists with lender comparison,
                application preparation, income documentation, legal
                coordination, technical valuation and processing updates.
              </p>

              <div className="lap-benefit-list">
                {[
                  "Indicative rates starting from 7.90%*",
                  "Residential and commercial property options",
                  "Solutions for salaried and self-employed applicants",
                  "Flexible tenure subject to lender policy",
                  "Property legal and technical coordination",
                  "Support from application to disbursement",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link to="/apply-now" className="lap-inline-cta">
                Check Your Eligibility
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* USES */}

        <section className="lap-uses-section">
          <div className="lap-page-container">
            <div className="lap-section-header">
              <span className="lap-section-eyebrow">
                Flexible Fund Usage
              </span>

              <h2>
                Common uses of a
                <span> Loan Against Property</span>
              </h2>

              <p>
                LAP funds can support a variety of eligible personal and
                business requirements, subject to lender terms.
              </p>
            </div>

            <div className="lap-uses-grid">
              {lapUses.map((item) => (
                <article className="lap-use-card" key={item.title}>
                  <span className="lap-use-icon">
                    <i className={item.icon}></i>
                  </span>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ELIGIBILITY */}

        <section className="lap-eligibility-section">
          <div className="lap-page-container">
            <div className="lap-section-header">
              <span className="lap-section-eyebrow">
                Basic Eligibility
              </span>

              <h2>
                Loan Against Property
                <span> eligibility factors</span>
              </h2>

              <p>
                The lender evaluates both the applicant’s repayment profile and
                the legal and technical condition of the property.
              </p>
            </div>

            <div className="lap-eligibility-grid">
              {eligibilityItems.map((item) => (
                <article
                  className="lap-eligibility-card"
                  key={item.title}
                >
                  <div className="lap-eligibility-image">
                    <img
                      src={item.image}
                      alt={item.alt}
                      loading="lazy"
                    />
                  </div>

                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="lap-eligibility-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Final eligibility depends on lender policy, property
                acceptability, income assessment and complete documentation.
              </span>
            </div>
          </div>
        </section>

        {/* CTA */}

        <section className="lap-cta-section">
          <div className="lap-page-container">
            <div className="lap-cta-card">
              <div>
                <span>Start Your Application</span>

                <h2>
                  Need funds against your eligible property?
                </h2>

                <p>
                  Share your property and income details to explore suitable
                  Loan Against Property options.
                </p>
              </div>

              <Link to="/apply-now" className="lap-primary-btn">
                Apply for LAP
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* DOCUMENTS */}

        <section className="lap-documents-section">
          <div className="lap-page-container">
            <div className="lap-section-header">
              <span className="lap-section-eyebrow">
                Documents Checklist
              </span>

              <h2>
                Documents generally required for
                <span> Loan Against Property</span>
              </h2>

              <p>
                The exact checklist varies according to the applicant profile,
                property type and selected lender.
              </p>
            </div>

            <div className="lap-documents-grid">
              {documentGroups.map((group) => (
                <article
                  className="lap-document-card"
                  key={group.title}
                >
                  <div className="lap-document-heading">
                    <span>
                      <img
                        src={group.image}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                      />
                    </span>

                    <h3>{group.title}</h3>
                  </div>

                  <ul>
                    {group.items.map((item) => (
                      <li key={item}>
                        <i className="bi bi-check-circle"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}

        <section className="lap-process-section">
          <div className="lap-page-container">
            <div className="lap-section-header">
              <span className="lap-section-eyebrow">
                Application Journey
              </span>

              <h2>
                A structured
                <span> LAP application process</span>
              </h2>
            </div>

            <div className="lap-process-grid">
              {applicationSteps.map((step, index) => (
                <article
                  className="lap-process-card"
                  key={step.title}
                >
                  <span className="lap-process-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* RATE TABLE */}

        <section id="lap-rates" className="lap-rates-section">
          <div className="lap-page-container">
            <div className="lap-section-header">
              <span className="lap-section-eyebrow">
                Indicative Bank Comparison
              </span>

              <h2>
                Loan Against Property
                <span> interest-rate overview</span>
              </h2>

              <p>
                Rates and charges are indicative and must be confirmed with the
                selected lender before applying.
              </p>
            </div>

            <div className="lap-table-wrapper">
              <table className="lap-rate-table">
                <thead>
                  <tr>
                    <th>Bank</th>
                    <th>Starting Rate</th>
                    <th>Indicative Amount</th>
                    <th>Maximum Tenure</th>
                    <th>Processing Fee</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {lapRates.map((bank) => (
                    <tr key={bank.bank}>
                      <td>
                        <a
                          href={bank.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {bank.bank}
                          <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                      </td>

                      <td>
                        <strong>{bank.rate}</strong>
                      </td>

                      <td>{bank.amount}</td>
                      <td>{bank.tenure}</td>
                      <td>{bank.fee}</td>

                      <td>
                        <Link
                          to="/apply-now"
                          className="lap-table-apply-btn"
                        >
                          Apply
                          <i className="bi bi-arrow-right"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lap-table-note">
              <i className="bi bi-info-circle"></i>

              <span>
                Rates, fees, property eligibility and loan limits may change
                according to lender policy.
              </span>
            </div>
          </div>
        </section>

        {/* FEES */}

        <section className="lap-fee-section">
          <div className="lap-page-container lap-fee-grid">
            <div className="lap-fee-image">
              <img
                src="/images/FrontPage/loan-dealing.jpg"
                alt="Loan Against Property processing"
                loading="lazy"
              />
            </div>

            <div className="lap-fee-content">
              <span className="lap-section-eyebrow">
                Loan Charges
              </span>

              <h2>
                Understand the
                <span> processing and property charges</span>
              </h2>

              <p>
                Lenders may charge fees for application processing, legal title
                review, technical valuation, documentation and other services.
              </p>

              <p>
                The amount varies according to the lender, loan amount,
                property type, applicant profile and available offer.
                Applicable taxes may also be charged.
              </p>

              <div className="lap-fee-highlight">
                <i className="bi bi-receipt"></i>

                <div>
                  <strong>
                    Review the complete lender fee schedule
                  </strong>

                  <span>
                    Confirm processing, legal, valuation, mortgage,
                    documentation and other applicable charges.
                  </span>
                </div>
              </div>

              <Link to="/apply-now" className="lap-inline-cta">
                Get Suitable LAP Options
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* PROPERTY VALUE */}

        <section className="lap-property-section">
          <div className="lap-page-container lap-property-grid">
            <div className="lap-property-content">
              <span className="lap-section-eyebrow">
                Property-Based Funding
              </span>

              <h2>
                Your property may support your
                <span> financial requirements</span>
              </h2>

              <p>
                Eligible funding is determined through legal verification,
                technical valuation, property type, location, ownership,
                marketability and the applicant’s repayment capacity.
              </p>

              <div className="lap-benefit-list">
                {[
                  "Residential property options",
                  "Commercial property options",
                  "Business and personal end-use support",
                  "Flexible tenure subject to lender policy",
                  "Property valuation coordination",
                  "Balance transfer options for eligible borrowers",
                ].map((item) => (
                  <div key={item}>
                    <span>
                      <i className="bi bi-check-lg"></i>
                    </span>

                    {item}
                  </div>
                ))}
              </div>

              <Link to="/apply-now" className="lap-inline-cta">
                Evaluate Your Property
                <i className="bi bi-arrow-right"></i>
              </Link>
            </div>

            <div className="lap-property-image">
              <img
                src="/images/Bank Pages/luxuryhome.png"
                alt="Property offered as loan collateral"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* FAQ */}

        <section className="lap-faq-section">
          <div className="lap-page-container">
            <div className="lap-section-header">
              <span className="lap-section-eyebrow">
                Common Questions
              </span>

              <h2>
                Loan Against Property
                <span> frequently asked questions</span>
              </h2>
            </div>

            <div className="lap-faq-layout">
              <div className="lap-faq-list">
                {faqItems.map((faq, index) => {
                  const isOpen = openFaq === index;

                  return (
                    <article
                      className={`lap-faq-item ${
                        isOpen ? "open" : ""
                      }`}
                      key={faq.question}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenFaq(isOpen ? null : index)
                        }
                        aria-expanded={isOpen}
                      >
                        <span className="lap-faq-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span>{faq.question}</span>

                        <i
                          className={
                            isOpen
                              ? "bi bi-dash-lg"
                              : "bi bi-plus-lg"
                          }
                        ></i>
                      </button>

                      {isOpen && (
                        <div className="lap-faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <aside className="lap-help-card">
                <span className="lap-help-icon">
                  <i className="bi bi-headset"></i>
                </span>

                <span className="lap-help-eyebrow">
                  Need Assistance?
                </span>

                <h3>
                  Speak with our Loan Against Property team
                </h3>

                <p>
                  Share your property type, location, income profile and
                  required loan amount to understand suitable next steps.
                </p>

                <Link to="/contact" className="lap-primary-btn">
                  Contact Our Team
                  <i className="bi bi-arrow-right"></i>
                </Link>

                <Link
                  to="/apply-now"
                  className="lap-help-secondary-btn"
                >
                  <i className="bi bi-file-earmark-text"></i>
                  Apply Online
                </Link>
              </aside>
            </div>
          </div>
        </section>

        {/* DISCLAIMER */}

        <section className="lap-final-disclaimer">
          <div className="lap-page-container">
            <i className="bi bi-shield-exclamation"></i>

            <p>
              *Displayed rates, amounts and product information are
              indicative. Final approval, rate, tenure, loan amount, fees,
              property acceptability and other terms are determined by the
              selected lender after complete applicant and property
              assessment. MONAYRA (OPC) PRIVATE LIMITED provides loan
              assistance and does not guarantee approval.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

export default LoanAgainstProperty;